import React, { useState, useCallback, useMemo } from 'react';
// The Clipboard icon is now an inline SVG to resolve 'lucide-react' dependency errors.

// --- TypeScript Data Structures (Interfaces) ---

/** Defines the structure for a single letter template. */
interface LetterTemplate {
  id: number;
  title: string;
  emphasis: string;
  template: string;
}

/** Defines the structure for the single child's custom data. */
interface FormData {
  name: string;
  age: number | string; // Allow string for input, but use number for generation
  niceActivity: string;
  goodDeed: string;
  grownUpTitle: string;
  templateId: number;
}

// --- Letter Templates Definition (Typed Object) ---

const SANTA_LETTER_TEMPLATES: LetterTemplate[] = [
  // Placeholders: {{CHILD_NAME}}, {{CHILD_AGE}}, {{NICE_ACTIVITY}}, {{GOOD_DEED}}, {{GROWN_UP_TITLE}}
  { id: 1, title: "1. The Classic North Pole Message", emphasis: "Classic & Traditional", template: `Dearest {{CHILD_NAME}},

Ho ho ho! I hope this special message finds you well! I'm writing to you all the way from my workshop here at the **North Pole**. It's a very busy place right now!

I hear you've just turned {{CHILD_AGE}} (or are about to). My goodness, you are growing up so fast! It seems like just yesterday you were {{NICE_ACTIVITY}}.

I've been keeping a close eye on my List, and I see you've been working hard to stay on the **Nice List** this year. My head elf, Jingle, showed me how you were a **big helper** when you {{GOOD_DEED}}. That makes me so proud! Being kind and trying your best is the most important magic of all.

Right now, the elves are making toys faster than a speeding sleigh, and the reindeer are practicing their takeoffs! Remember to keep shining bright, listening to your {{GROWN_UP_TITLE}}, and going to bed early.

Keep up the great work, my dear {{CHILD_NAME}}!

Yours forever and always, Santa Claus.` },
  { id: 2, title: "2. A Note on Kindness & Sharing", emphasis: "Focus on Empathy", template: `Hello there, {{CHILD_NAME}}!

Mrs. Claus and I wanted to send a quick note about your wonderful heart. We saw how kindly you behaved when you {{GOOD_DEED}}. Kindness is the greatest gift of all! It's better than any toy.

I also saw you've been practicing {{NICE_ACTIVITY}}—that's dedication! Keep listening to your {{GROWN_UP_TITLE}} and spreading cheer. Merry Christmas, from your friend in red!` },
  { id: 3, title: "3. Christmas Eve Excitement", emphasis: "Building Anticipation", template: `Ho Ho Ho! The sleigh bells are getting polished, {{CHILD_NAME}}!

Can you believe Christmas Eve is almost here? I've heard you've been working hard on {{NICE_ACTIVITY}}, which is excellent! My team is loading up the sleigh, and I can't wait to fly over your roof.

It's clear from {{GOOD_DEED}} that you deserve a spot right at the top of the Nice List. Be sure to be extra good for your {{GROWN_UP_TITLE}} right up until bedtime! We'll see you soon!` },
];

/**
 * Finds a template by its ID.
 * @param id - The template ID.
 * @returns The template object or the first one as default.
 */
const getTemplateById = (id: number): LetterTemplate => SANTA_LETTER_TEMPLATES.find(t => t.id === id) || SANTA_LETTER_TEMPLATES[0];


// --- Main App Component ---
export default function App_Santa_v01(): JSX.Element {
  
  // Initial state for the single child's data
  const [formData, setFormData] = useState<FormData>({
    name: 'Lily',
    age: 7,
    niceActivity: 'learning to ride their bike without training wheels',
    goodDeed: 'helped their neighbor shovel snow',
    grownUpTitle: 'Grown-Ups',
    templateId: 1,
  });
  
  const [copyStatus, setCopyStatus] = useState<boolean>(false);

  /**
   * Generates a letter using the current form data and selected template.
   */
  const generateLetterText = useCallback((data: FormData, templateData: LetterTemplate): string => {
    // Ensure age is treated as a string for replacement, even if stored as a number/string
    const childAge = typeof data.age === 'number' ? data.age.toString() : data.age;

    if (!data.name || !childAge) {
      return "Please complete the child's name and age to generate the letter!";
    }
    
    let letter = templateData.template;

    letter = letter.replace(/{{CHILD_NAME}}/g, data.name);
    letter = letter.replace(/{{CHILD_AGE}}/g, childAge);
    letter = letter.replace(/{{NICE_ACTIVITY}}/g, data.niceActivity);
    letter = letter.replace(/{{GOOD_DEED}}/g, data.goodDeed);
    letter = letter.replace(/{{GROWN_UP_TITLE}}/g, data.grownUpTitle);

    return letter;
  }, []);

  /**
   * Handles form input changes.
   * @param e - The change event, strictly typed.
   */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      // Safely parse number for age field
      [name]: name === 'age' ? (parseInt(value) || '') : value,
    }));
  };

  /**
   * Handles template selection.
   */
  const handleTemplateSelection = (templateId: number): void => {
    setFormData(prev => ({ ...prev, templateId }));
  };

  /**
   * Memoized data for the current letter preview.
   */
  const currentTemplate: LetterTemplate = useMemo(() => {
    return getTemplateById(formData.templateId);
  }, [formData.templateId]);

  /**
   * Memoized data for the final generated letter text.
   */
  const finalLetter: string = useMemo(() => {
    return generateLetterText(formData, currentTemplate);
  }, [formData, currentTemplate, generateLetterText]);

  /**
   * Copies the final generated letter text to the clipboard.
   */
  const copyToClipboard = (): void => {
    try {
      const el = document.createElement('textarea');
      el.value = finalLetter;
      document.body.appendChild(el);
      el.select();
      // Using execCommand for better cross-browser compatibility in iFrames
      document.execCommand('copy'); 
      document.body.removeChild(el);
      setCopyStatus(true);
      setTimeout(() => setCopyStatus(false), 3000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };


  // --- Input Form Fields Component ---
  const InputFields = (): JSX.Element => (
    <div className="space-y-5">
      <h3 className="text-xl font-bold text-red-700 mb-3 border-b pb-2 border-red-200">
        1. Child Information
      </h3>

      {/* Child's Name */}
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
          Child's Full Name
        </label>
        <input
          type="text"
          name="name"
          id="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="Enter child's name"
          required
          className="w-full p-3 border border-gray-300 rounded-xl focus:ring-red-500 focus:border-red-500 text-gray-900"
        />
      </div>

      {/* Child's Age & Grown-Up Title */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="age" className="block text-sm font-medium text-gray-700 mb-1">
            Child's Age
          </label>
          <input
            type="number"
            name="age"
            id="age"
            value={formData.age}
            onChange={handleChange}
            min="1"
            max="100"
            required
            className="w-full p-3 border border-gray-300 rounded-xl focus:ring-red-500 focus:border-red-500 text-gray-900"
          />
        </div>
        <div>
          <label htmlFor="grownUpTitle" className="block text-sm font-medium text-gray-700 mb-1">
            Adult's Title (To be mentioned in the letter)
          </label>
          <select
            name="grownUpTitle"
            id="grownUpTitle"
            value={formData.grownUpTitle}
            onChange={handleChange}
            className="w-full p-3 border border-gray-300 rounded-xl focus:ring-red-500 focus:border-red-500 text-gray-900"
          >
            <option value="Mom">Mom</option>
            <option value="Dad">Dad</option>
            <option value="Grandma">Grandma</option>
            <option value="Grandpa">Grandpa</option>
            <option value="Grown-Ups">Grown-Ups (default)</option>
          </select>
        </div>
      </div>

      {/* Simple Nice Activity (Textarea fix applied) */}
      <div>
        <label htmlFor="niceActivity" className="block text-sm font-medium text-gray-700 mb-1">
          Simple Nice Activity (e.g., 'learned to tie their shoes')
        </label>
        <textarea
          name="niceActivity"
          id="niceActivity"
          rows={2} // TS2322 fix: using number expression
          value={formData.niceActivity}
          onChange={handleChange}
          placeholder="e.g., learned to sing the ABCs"
          className="w-full p-3 border border-gray-300 rounded-xl focus:ring-red-500 focus:border-red-500 text-gray-900"
        ></textarea>
      </div>

      {/* Specific Good Deed (Textarea fix applied) */}
      <div>
        <label htmlFor="goodDeed" className="block text-sm font-medium text-gray-700 mb-1">
          Specific **Good Deed** (The 'Big Helper' part!)
        </label>
        <textarea
          name="goodDeed"
          id="goodDeed"
          rows={2} // TS2322 fix: using number expression
          value={formData.goodDeed}
          onChange={handleChange}
          placeholder="e.g., shared their favorite toy with their little sister"
          className="w-full p-3 border border-gray-300 rounded-xl focus:ring-red-500 focus:border-red-500 text-gray-900"
        ></textarea>
      </div>
    </div>
  );
  
  // --- Template Selector Component ---
  const TemplateSelector = (): JSX.Element => (
    <div className="mt-6 pt-4 border-t border-red-200">
      <h3 className="text-xl font-bold text-red-700 mb-3 border-b pb-2 border-red-200">
        2. Choose Letter Template
      </h3>
      <div className="space-y-2">
        {SANTA_LETTER_TEMPLATES.map((template: LetterTemplate) => (
          <div
            key={template.id}
            className={`p-3 rounded-xl cursor-pointer transition-all border ${
              template.id === formData.templateId
                ? 'bg-red-100 border-red-500 shadow-md ring-2 ring-red-500'
                : 'bg-gray-50 hover:bg-gray-100 border-gray-200'
            }`}
            onClick={() => handleTemplateSelection(template.id)}
          >
            <p className={`font-semibold ${template.id === formData.templateId ? 'text-red-700' : 'text-gray-800'}`}>
              {template.title}
            </p>
            <p className="text-xs text-gray-500 italic">({template.emphasis})</p>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen p-4 sm:p-8 bg-gradient-to-br from-red-700 via-red-800 to-red-900 font-inter text-gray-100">
      <div className="max-w-6xl mx-auto">
        <header className="text-center mb-10 p-4 rounded-xl bg-white bg-opacity-10 shadow-lg">
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-yellow-300 drop-shadow-lg">
            🎅 Santa Letter Generator (Classic)
          </h1>
          <p className="mt-2 text-lg text-red-200">
            Customize a magical letter for one special child.
          </p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* --- Input Form (Left Side) --- */}
          <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-2xl border-4 border-yellow-300 h-fit">
            <h2 className="text-2xl font-bold text-red-800 mb-6 border-b pb-2 border-red-200">
              Personalization Center
            </h2>
            
            <InputFields />
            <TemplateSelector />
            
          </div>

          {/* --- Generated Letter Output (Right Side) --- */}
          <div className="relative bg-white p-6 sm:p-8 rounded-2xl shadow-2xl border-4 border-green-300">
            <h2 className="text-2xl font-bold text-green-800 mb-6 border-b pb-2 border-green-200">
              Letter Preview & Download
            </h2>
            <p className="text-sm text-gray-600 mb-4">
                **Preview Template:** {currentTemplate.title}
            </p>

            {/* Letter Display Area */}
            <div className="overflow-y-auto max-h-[70vh] p-4 bg-red-50 border border-red-200 rounded-xl text-gray-900 whitespace-pre-wrap font-serif text-base leading-relaxed shadow-inner">
              {finalLetter}
            </div>

            <div className="mt-4 flex flex-col sm:flex-row justify-between items-center space-y-3 sm:space-y-0">
              <button
                onClick={copyToClipboard}
                className="w-full sm:w-auto flex items-center justify-center px-6 py-3 bg-yellow-500 text-white font-bold rounded-xl hover:bg-yellow-600 transition-colors shadow-lg hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-yellow-400"
              >
                {/* Clipboard Icon (Inline SVG) - Resolves TS2307 */}
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 mr-2"><rect width="8" height="4" x="8" y="2" rx="1" ry="1"></rect><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path></svg>
                {copyStatus ? 'Copied!' : `Copy Letter`}
              </button>
              {copyStatus && (
                <span className="text-sm text-yellow-300 bg-green-700 px-3 py-1 rounded-full shadow-md animate-pulse">
                  Text copied to clipboard!
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
